import { onBeforeUnmount, ref, type Ref } from 'vue';

/**
 * Реактивный флаг `prefers-reduced-motion: reduce`.
 *
 * Композейбл для Vue-частей анимаций (CountUp, scroll-reveal, toast slide-in и т.п.).
 * CSS-часть обрабатывается глобально в `styles/base.css`.
 *
 * @returns `Ref<boolean>` — true, когда пользователь попросил уменьшить анимации.
 */
export function useReducedMotion(): Ref<boolean> {
  const state = ref(false);

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return state;
  }

  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  state.value = mq.matches;

  const handler = (event: MediaQueryListEvent): void => {
    state.value = event.matches;
  };

  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
    onBeforeUnmount(() => mq.removeEventListener('change', handler));
  } else {
    // Safari < 14: MediaQueryList использует устаревший addListener.
    const legacy = mq as MediaQueryList & {
      addListener: (l: (e: MediaQueryListEvent) => void) => void;
      removeListener: (l: (e: MediaQueryListEvent) => void) => void;
    };
    legacy.addListener(handler);
    onBeforeUnmount(() => legacy.removeListener(handler));
  }

  return state;
}
